import React, { useState } from "react"
import styles from "./styles.module.scss"
import { Avatar, Badge, Button, Card, Flex, Modal, Tooltip } from "antd"
import { useEventId } from "pages/events"
import { useEvent } from "pages/events/queries"
import userAvatar from "assets/user.jpg"
import { ReactComponent as CheckIcon } from "assets/icons/check.svg"
import { ReactComponent as LeaveIcon } from "assets/icons/leave.svg"
import { ReactComponent as CommentIcon } from "assets/icons/comment.svg"
import { ReactComponent as AccidentIcon } from "assets/icons/accident.svg"
import { ReactComponent as WarningIcon } from "assets/icons/warning.svg"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import { ReactComponent as RefreshIcon } from "assets/icons/refresh.svg"
import { ReactComponent as AbsenceIcon } from "assets/icons/absence.svg"
import { ReactComponent as PresentIcon } from "assets/icons/presence.svg"
import { ReactComponent as HelpIcon } from "assets/icons/help-buoy.svg"

import { useTranslation } from "react-i18next"
import { IMemberItemProps } from "./types"
import { useNavigate } from "react-router-dom"
import { useToggleAbsence } from "pages/events/members/queries"

const MemberItem: React.FC<IMemberItemProps> = ({
  item,
  isLoading,
  unsubscribeMemberModal,
  plannedAbsenceModal,
  presenceDrawer,
  feedbackDrawer,
  bathroomHelpModal,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const eventId = useEventId()
  const event = useEvent(eventId!)
  const [avatarThumbModal, setAvatarThembModal] = useState(false)
  const plannedAbsence = useToggleAbsence(eventId!)

  return (
    <Card {...props} className={styles.memberCard}>
      <Card.Grid
        hoverable={false}
        style={{
          width: "30%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Avatar
          src={
            event.data?.status === "execution"
              ? item.member.photo
              : (item as any).photo || userAvatar
          }
          size={60}
          onClick={() => setAvatarThembModal(true)}
          style={{ cursor: "pointer" }}
        />
      </Card.Grid>
      <Card.Grid
        hoverable={false}
        style={{
          width: "70%",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          rowGap: "var(--size-2)",
        }}
      >
        <Flex gap="middle" vertical>
          <p className="subheader-heavy">
            {event.data?.status === "execution"
              ? item.member.name
              : (item as any).name}
          </p>
          <Flex gap="small" wrap="wrap">
            <Button onClick={() => navigate(item.id)} icon={<InfoIcon />} />
            {item.planned_absent && (
              <Tooltip
                title={t("members.planified_abscent_message")}
                placement="top"
              >
                <WarningIcon className={styles.abscentIcon} />
              </Tooltip>
            )}
            {event.data?.status === "execution" && (
              <>
                {(item.transitions?.find(
                  (e) => e.name === "planned->checkin"
                ) ||
                  item.transitions?.find(
                    (e) => e.name === "checkout->checkin"
                  )) && (
                  <Button
                    disabled={isLoading}
                    onClick={() =>
                      presenceDrawer.open({
                        transition: `${item.status}->checkin`,
                        presenceId: item.id,
                        member: item.member,
                      })
                    }
                    icon={
                      item.status === "planned" ? (
                        <CheckIcon />
                      ) : (
                        <RefreshIcon />
                      )
                    }
                  />
                )}
                {item.transitions?.find(
                  (e) => e.name === "checkin->checkout"
                ) && (
                  <Button
                    disabled={isLoading}
                    onClick={() =>
                      presenceDrawer.open({
                        transition: `${item.status}->checkout`,
                        presenceId: item.id,
                        member: item.member,
                      })
                    }
                    icon={<LeaveIcon />}
                  />
                )}

                <Badge count={!!item.feedback ? 1 : 0} offset={[-6, 0]}>
                  <Button
                    onClick={() => feedbackDrawer.open(item)}
                    icon={<CommentIcon />}
                  />
                </Badge>
                <Badge count={item.accidents_count} offset={[-6, 0]}>
                  <Button
                    onClick={() => navigate(`${item.id}/accidents`)}
                    icon={<AccidentIcon />}
                  />
                </Badge>
                <Badge>
                  <Button
                    disabled={
                      item.status !== "planned" || plannedAbsence.isLoading
                    }
                    onClick={() =>
                      item.planned_absent
                        ? plannedAbsence.mutate({ member_id: item.member_id })
                        : plannedAbsenceModal(item.member_id)
                    }
                    icon={
                      item.planned_absent ? <PresentIcon /> : <AbsenceIcon />
                    }
                    loading={plannedAbsence.isLoading}
                  />
                </Badge>

                <Badge>
                  <Button
                    onClick={() => bathroomHelpModal(item.id)}
                    icon={<HelpIcon />}
                    loading={plannedAbsence.isLoading}
                  />
                </Badge>

                {item.attendance_on_execution && (
                  <Button
                    disabled={item.status !== "planned"}
                    onClick={() => unsubscribeMemberModal(item.member_id)}
                    icon={<DeleteIcon />}
                  />
                )}
              </>
            )}
          </Flex>
        </Flex>
      </Card.Grid>
      <Modal
        open={avatarThumbModal}
        onCancel={() => setAvatarThembModal(false)}
        footer={null}
        styles={{
          content: { backgroundColor: "transparent", boxShadow: "none" },
        }}
      >
        <Flex justify="center" align="center">
          <Avatar
            src={
              event.data?.status === "execution"
                ? item.member.photo
                : (item as any).photo || userAvatar
            }
            size={300}
          />
        </Flex>
      </Modal>
    </Card>
  )
}

export default MemberItem
